// Helpers para a timeline de steps datados da oferta (PropertyOffer).
// Espelha a OfferStatusList "offers-page-v2" da QuintoAndar: cada step pode
// estar pendente (azul/primary), concluído (verde/success), cancelado
// (vermelho/error) ou ignorado explicitamente (cinza/neutral).

export const STEP_STATE_COLORS = {
  pendente: 'primary',
  concluido: 'success',
  cancelado: 'error',
  ignorado: 'neutral',
};

// Classes Tailwind por estado, usadas no marcador e na linha de conexão.
export const STEP_STATE_CLASSES = {
  pendente: {
    dot: 'bg-n-brand text-white',
    text: 'text-n-slate-12',
    line: 'bg-n-weak',
  },
  concluido: {
    dot: 'bg-n-teal-9 text-white',
    text: 'text-n-slate-12',
    line: 'bg-n-teal-9',
  },
  cancelado: {
    dot: 'bg-n-ruby-9 text-white',
    text: 'text-n-ruby-11',
    line: 'bg-n-ruby-9',
  },
  ignorado: {
    dot: 'bg-n-slate-6 text-n-slate-11',
    text: 'text-n-slate-10',
    line: 'bg-n-weak',
  },
};

export const STEP_ICON_CLASSES = {
  new: 'i-lucide-file-text',
  submitted: 'i-lucide-file-text',
  negotiation: 'i-lucide-message-circle',
  accepted: 'i-lucide-circle-check',
  credit: 'i-lucide-search',
  documentation: 'i-lucide-file-check-2',
  guarantee: 'i-lucide-shield-check',
  ccv: 'i-lucide-file-signature',
  contract: 'i-lucide-file-signature',
  signed: 'i-lucide-party-popper',
  finished: 'i-lucide-party-popper',
  canceled: 'i-lucide-circle-alert',
  declined: 'i-lucide-circle-alert',
  withdrawn: 'i-lucide-circle-alert',
};

const REFERENCE_STEP_COPY = Object.freeze({
  tenantDidntSentOffer: Object.freeze({
    label: 'Interessado não finalizou envio da proposta',
    description:
      'Interessado deu início a proposta mas falta finalizar o envio',
  }),
  waitingOwnerResponse: Object.freeze({
    label: 'Aguardando resposta do proprietário',
    description: 'Proprietário precisa responder a proposta',
  }),
  waitingTenantRespondCounterOffer: Object.freeze({
    label: 'Contra-proposta feita pelo proprietário',
    description: 'Interessado precisa responder a contra-proposta',
  }),
  waitingOwnerRespondCounterOffer: Object.freeze({
    label: 'Contra-proposta feita pelo interessado',
    description: 'Proprietário precisa responder a contra-proposta',
  }),
  ownerRejectedOffer: Object.freeze({
    label: 'Proposta rejeitada',
  }),
  tenantRejectedOffer: Object.freeze({
    label: 'Proposta rejeitada',
  }),
  waitingForTenantToDecide: Object.freeze({
    label: 'Aguardando interessado decidir sobre imóvel',
  }),
  offerDemandPending: Object.freeze({
    label: 'Aguardando resposta do cliente',
  }),
  offerAccepted: Object.freeze({
    label: 'Proposta aceita',
  }),
  proposalCancelled: Object.freeze({
    label: 'Proposta cancelada',
  }),
  stalledRejection: Object.freeze({
    label: 'Proposta cancelada',
  }),
  houseInactive: Object.freeze({
    label: 'Proposta cancelada',
    description: 'Proposta cancelada por suspensão do imóvel',
  }),
  houseUnpublished: Object.freeze({
    label: 'Proposta cancelada',
    description: 'Proposta cancelada por despublicação do imóvel',
  }),
  docsDemandPending: Object.freeze({
    label: 'Documentos pendentes do cliente',
    description: 'Interessado precisa enviar a documentação',
  }),
  waitingDocumentations: Object.freeze({
    label: 'Aguardando envio de documentos',
    description: 'Aguardando envio de documentos',
  }),
  waitingTenantDocumentation: Object.freeze({
    label: 'Aguardando documentos do interessado',
    description: 'Interessado precisa enviar a documentação',
  }),
  waitingOwnerDocumentation: Object.freeze({
    label: 'Aguardando documentos do proprietário',
    description: 'Proprietário precisa enviar a documentação',
  }),
  waitingResendDocumentations: Object.freeze({
    label: 'Aguardando reenvio de documentos',
    description: 'Aguardando reenvio de documentos',
  }),
  waitingTenantResendDocumentation: Object.freeze({
    label: 'Aguardando interessado reenviar documentação',
    description: 'Interessado precisa reenviar a documentação',
  }),
  waitingOwnerResendDocumentation: Object.freeze({
    label: 'Aguardando proprietário reenviar documentação',
    description: 'Proprietário precisa reenviar a documentação',
  }),
  docsDemandResend: Object.freeze({
    label: 'Documentos com pendência',
    description: 'Interessado precisa reenviar a documentação',
  }),
  docsSupplyPending: Object.freeze({
    label: 'Documentos pendentes do proprietário',
    description: 'Proprietário precisa enviar a documentação',
  }),
  docsQuintoandarAnalysis: Object.freeze({
    label: 'Documentação em análise',
    description: 'Interessado com documentação em análise (3 dias úteis)',
  }),
  documentationsInAnalysis: Object.freeze({
    label: 'Documentações em análise',
    description: 'Documentações em análise',
  }),
  tenantDocumentationInAnalysisChip: Object.freeze({
    label: 'Documentos do interessado em análise',
    description: 'Interessado com documentação em análise (3 dias úteis)',
  }),
  ownerDocumentationInAnalysisChip: Object.freeze({
    label: 'Documentos do proprietário em análise',
    description: 'Proprietário com documentação em análise (3 dias úteis)',
  }),
  docsApproved: Object.freeze({
    label: 'Documentação aprovada',
    description: 'Documentação pronta para o contrato',
  }),
  documentationsRefused: Object.freeze({
    label: 'Documentações recusadas',
    description: 'Documentações recusadas',
  }),
  tenantRejected: Object.freeze({
    label: 'Recusado na análise de crédito',
    description: 'Recusado na análise de crédito',
  }),
  tenantWithMultipleDocs: Object.freeze({
    label: 'Interessado com documentação em mais de um imóvel',
    description: 'Interessado com documentação em mais de um imóvel',
  }),
  guaranteeCreated: Object.freeze({
    label: 'Garantia emitida',
  }),
  guaranteeDemandPaymentPending: Object.freeze({
    label: 'Aguardando pagamento da garantia',
  }),
  guaranteePaid: Object.freeze({
    label: 'Garantia paga',
  }),
  waitingContract: Object.freeze({
    label: 'Aguardando contrato',
  }),
  contractSigned: Object.freeze({
    label: 'Contrato assinado',
  }),
  contractTerminated: Object.freeze({
    label: 'Contrato finalizado',
  }),
  contractCancelled: Object.freeze({
    label: 'Contrato cancelado',
  }),
  contractQuintoandarInCreation: Object.freeze({
    label: 'Contrato em elaboração',
  }),
  contractAllSignaturesPending: Object.freeze({
    label: 'Contrato aguardando assinaturas',
    description: 'Aguardando ambas assinaturas',
  }),
  contractSupplySignaturePending: Object.freeze({
    label: 'Aguardando assinatura do proprietário',
    description: 'Proprietário precisa assinar o contrato',
  }),
  contractDemandSignaturePending: Object.freeze({
    label: 'Aguardando assinatura do cliente',
    description: 'Interessado precisa assinar o contrato',
  }),
  waitingSignaturesValidation: Object.freeze({
    label: 'Aguardando validação das assinaturas',
    description: 'Aguardando validação das assinaturas',
  }),
  ownerSignedContract: Object.freeze({
    label: 'Proprietário finalizou a assinatura do contrato',
  }),
  tenantSignedContract: Object.freeze({
    label: 'Interessado finalizou a assinatura do contrato',
  }),
  contractStartDateReached: Object.freeze({
    label: 'Contrato ativo',
  }),
});

// Mapeia um estado de step para a cor semântica (primary/success/error).
export const colorForState = state =>
  STEP_STATE_COLORS[state] || STEP_STATE_COLORS.pendente;

// Mapeia um estado de step para as classes visuais, com fallback seguro.
export const classesForState = state =>
  STEP_STATE_CLASSES[state] || STEP_STATE_CLASSES.pendente;

export const iconForStep = step => {
  const key = String(
    step?.key || step?.status || step?.label || ''
  ).toLowerCase();
  if (step?.state === 'cancelado') return STEP_ICON_CLASSES.canceled;
  if (key.includes('credit') || key.includes('credito')) {
    return STEP_ICON_CLASSES.credit;
  }
  if (key.includes('doc') || key.includes('document') || key.includes('kyc')) {
    return STEP_ICON_CLASSES.documentation;
  }
  if (key.includes('guarantee') || key.includes('garantia')) {
    return STEP_ICON_CLASSES.guarantee;
  }
  if (key.includes('negotiation') || key.includes('negoci')) {
    return STEP_ICON_CLASSES.negotiation;
  }
  if (key.includes('accept') || key.includes('aceit')) {
    return STEP_ICON_CLASSES.accepted;
  }
  if (
    key.includes('ccv') ||
    key.includes('contract') ||
    key.includes('contrato') ||
    key.includes('signature') ||
    key.includes('assinatura')
  ) {
    return STEP_ICON_CLASSES.ccv;
  }
  if (
    key.includes('signed') ||
    key.includes('finish') ||
    key.includes('finaliz')
  ) {
    return STEP_ICON_CLASSES.finished;
  }
  if (
    key.includes('declin') ||
    key.includes('withdraw') ||
    key.includes('cancel')
  ) {
    return STEP_ICON_CLASSES.canceled;
  }

  return STEP_ICON_CLASSES.new;
};

// Normaliza um step vindo da API garantindo estado/cor consistentes mesmo se a
// API só enviar datas.
export const normalizeStep = (step = {}) => {
  let state = step.state;
  const key = step.key || step.status || step.name;
  const referenceCopy = REFERENCE_STEP_COPY[key] || {};
  const completedAt =
    step.completed_at || step.completedAt || step.startDate || step.start_date;
  const canceledAt = step.canceled_at || step.canceledAt;
  const skippedAt = step.skipped_at || step.skippedAt;
  const startedAt = step.started_at || step.startedAt;
  if (!state) {
    if (canceledAt) state = 'cancelado';
    else if (completedAt) state = 'concluido';
    else if (skippedAt) state = 'ignorado';
    else state = 'pendente';
  }
  return {
    key,
    label:
      step.label || step.title || referenceCopy.label || step.name || step.key,
    description:
      step.description ||
      step.subtitle ||
      step.message ||
      referenceCopy.description ||
      '',
    state,
    color: step.color || colorForState(state),
    current: Boolean(step.current),
    startedAt: startedAt || null,
    completedAt: completedAt || null,
    canceledAt: canceledAt || null,
    skippedAt: skippedAt || null,
  };
};
